import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import {
  StrengthMerge, StrengthMergeDecision, StrengthMergeService,
} from './strength-merge.service';

/**
 * Auflösung eines Kraft-Konflikts: getipptes Protokoll gegen FIT-Datei.
 *
 * ── Warum PAUSCHAL je Einheit und nicht je Satz ────────────────────────────
 *
 * Die Datei kennt keine Übungsnamen. In allen geprüften Dateien waren
 * `workout_step` und `exercise_title` leer; es gibt nur numerische
 * Kategoriecodes, von denen sich ein Viertel gar nicht auflöst. Eine
 * dreispaltige Diff-Tabelle behauptete damit eine Zeilenpaarung zwischen
 * „Bankdrücken" und `[65534, 7]`, die es nicht gibt.
 *
 * Die richtige Frage ist deshalb dieselbe wie bei der Doppelaufzeichnung:
 * **welche Aufzeichnung soll zählen?** Zwei Entscheidungskarten, der
 * Wertevergleich als Beleg darunter — Muster von
 * `DeviceDuplicateDialogComponent`, nicht von `DuplicateResolutionDialogComponent`
 * (dessen „merge = Import füllt leere Felder" ist bei einer Liste von Übungen
 * bedeutungslos).
 *
 * Nichts wird gelöscht: die nicht gewählte Seite bleibt gespeichert und die
 * Entscheidung ist umkehrbar.
 */
@Component({
  selector: 'app-strength-conflict-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, TranslateModule],
  template: `
    <h2 mat-dialog-title>
      {{ 'training_analysis.strength_merge.dialog.title' | translate }}
    </h2>

    <mat-dialog-content>
      <p class="sc__lead">
        {{ 'training_analysis.strength_merge.dialog.lead' | translate }}
      </p>

      @for (merge of merges; track merge.id) {
        <section class="sc__conflict" [class.sc--done]="done.has(merge.id)">
          <header class="sc__head">
            <mat-icon>fitness_center</mat-icon>
            <strong>{{ merge.date }}</strong>
            @if (merge.device_label) {
              <span class="sc__device">· {{ merge.device_label }}</span>
            }
          </header>

          @if (done.has(merge.id)) {
            <p class="sc__done">
              <mat-icon>check</mat-icon>
              {{ 'training_analysis.strength_merge.dialog.saved' | translate }}
            </p>
          } @else {
            <div class="sc__cards">
              @for (seite of ['manual', 'fit']; track seite) {
                <button
                  type="button"
                  class="sc__card"
                  [class.sc--busy]="busy === merge.id"
                  [disabled]="busy !== null"
                  (click)="choose(merge, $any(seite))">
                  <span class="sc__card-title">
                    <mat-icon>{{ seite === 'manual' ? 'edit_note' : 'watch' }}</mat-icon>
                    {{ ('training_analysis.strength_merge.side.' + seite) | translate }}
                  </span>
                  <span class="sc__figures">
                    <span>{{ 'training_analysis.strength_merge.sets'
                             | translate:{ count: side(merge, seite).sets } }}</span>
                    <span>{{ 'training_analysis.strength_merge.exercises'
                             | translate:{ count: side(merge, seite).exercises } }}</span>
                    @if (side(merge, seite).tonnage_kg) {
                      <span>{{ side(merge, seite).tonnage_kg | number:'1.0-0' }} kg</span>
                    }
                  </span>
                  <span class="sc__items">
                    @for (item of side(merge, seite).items; track item.exercise_name) {
                      <span class="sc__item">{{ item.exercise_name }} · {{ item.sets }}</span>
                    }
                    @if (!side(merge, seite).items.length) {
                      <span class="sc__item sc__item--empty">
                        {{ 'training_analysis.strength_merge.no_sets' | translate }}
                      </span>
                    }
                  </span>
                  <!-- Was NUR die eine Seite hat: das ist der eigentliche
                       Entscheidungsgrund. Die Datei kennt nie RPE oder Notizen. -->
                  <span class="sc__extras">
                    @if (side(merge, seite).has_weight) {
                      <span class="sc__chip">
                        {{ 'training_analysis.strength_merge.has_weight' | translate }}
                      </span>
                    }
                    @if (side(merge, seite).has_rpe) {
                      <span class="sc__chip">RPE</span>
                    }
                    @if (side(merge, seite).has_notes) {
                      <span class="sc__chip">
                        {{ 'training_analysis.strength_merge.has_notes' | translate }}
                      </span>
                    }
                  </span>
                </button>
              }
            </div>

            <button
              mat-button
              class="sc__dismiss"
              [disabled]="busy !== null"
              (click)="choose(merge, 'dismiss')">
              {{ 'training_analysis.strength_merge.dialog.dismiss' | translate }}
            </button>
          }
        </section>
      }

      @if (!merges.length) {
        <p class="sc__empty">{{ 'training_analysis.strength_merge.dialog.empty' | translate }}</p>
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <!-- app.close, nicht common.close: letzteres gibt es nicht, und ein
           fehlender Schlüssel erschiene als roher Text im Knopf. -->
      <button mat-button mat-dialog-close>
        {{ 'app.close' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .sc__lead { margin: 0 0 var(--space-16); color: var(--text-secondary); }
    .sc__conflict {
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
      padding: var(--space-12);
      margin-bottom: var(--space-12);
    }
    .sc--done { opacity: 0.6; }
    .sc__head {
      display: flex; align-items: center; gap: var(--space-8);
      margin-bottom: var(--space-12);
    }
    .sc__device { color: var(--text-muted); font-size: var(--fs-sm); }
    .sc__done {
      display: flex; align-items: center; gap: var(--space-6);
      margin: 0; color: var(--status-good-text);
    }
    .sc__cards { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-12); }
    @media (max-width: 600px) { .sc__cards { grid-template-columns: 1fr; } }
    .sc__card {
      display: flex; flex-direction: column; gap: var(--space-6);
      text-align: left; padding: var(--space-12);
      min-height: var(--tap-target-min);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
      background: var(--surface); color: inherit;
      cursor: pointer; font: inherit;
    }
    .sc__card:hover:not(:disabled) { background: var(--surface-hover); }
    .sc__card:disabled { cursor: default; opacity: 0.6; }
    .sc__card-title {
      display: flex; align-items: center; gap: var(--space-6); font-weight: 600;
    }
    .sc__figures {
      display: flex; flex-wrap: wrap; gap: var(--space-8);
      font-variant-numeric: tabular-nums; color: var(--text-secondary);
      font-size: var(--fs-sm);
    }
    .sc__items { display: flex; flex-direction: column; gap: 2px; font-size: var(--fs-xs); }
    .sc__item { color: var(--text-muted); }
    .sc__item--empty { font-style: italic; }
    .sc__extras { display: flex; flex-wrap: wrap; gap: var(--space-4); }
    .sc__chip {
      padding: 1px var(--space-6); border-radius: var(--radius-full);
      background: var(--surface-variant); font-size: var(--fs-2xs);
    }
    .sc__dismiss { margin-top: var(--space-8); }
    .sc__empty { color: var(--text-muted); }
  `],
})
export class StrengthConflictDialogComponent {
  private readonly svc = inject(StrengthMergeService);
  private readonly cdr = inject(ChangeDetectorRef);

  merges: StrengthMerge[];
  /** Welcher Konflikt gerade gespeichert wird — sperrt alle Karten. */
  busy: number | null = null;
  readonly done = new Set<number>();

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { merges: StrengthMerge[] },
    private readonly ref: MatDialogRef<StrengthConflictDialogComponent>,
  ) {
    this.merges = data?.merges ?? [];
  }

  side(merge: StrengthMerge, welche: string) {
    return welche === 'fit' ? merge.sides.fit : merge.sides.manual;
  }

  choose(merge: StrengthMerge, decision: StrengthMergeDecision): void {
    this.busy = merge.id;
    this.svc.resolve(merge.id, decision).subscribe({
      next: () => {
        this.done.add(merge.id);
        this.busy = null;
        // Erst schliessen, wenn ALLE entschieden sind — bei mehreren
        // Konflikten waere ein Schliessen nach dem ersten aergerlich.
        if (this.done.size === this.merges.length) {
          this.ref.close(true);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.busy = null;
        this.cdr.markForCheck();
      },
    });
  }
}
